<template>
  <div class="all-shipment-price-tooltip-component">
    <template v-if="shipment.fr_data">
      <Tooltip>
        <template #body>
          <div class="flex flex-col gap-1">
            <div v-if="shippingFee > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.payment.shipping_price')}:` }}</span>
              <span class="text-sm">{{ toPrice(shippingFee) }}</span>
            </div>
            <div v-if="shipment.fr_data.fee_delivery > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.payment.delivery_price')}:` }}</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.fee_delivery) }}</span>
            </div>
            <div v-if="shipment.fr_data.package_type_price > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.payment.package_type_price')}:` }}</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.package_type_price) }}</span>
            </div>
            <div v-if="shipment.fr_data.fee_consolidation > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.payment.consolidation_fee')}:` }}</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.fee_consolidation) }}</span>
            </div>
            <div v-if="shipment.fr_data.consolidated_storage_fee > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.payment.consolidated_package_storage_fee')}:` }}</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.consolidated_storage_fee) }}</span>
            </div>
            <div v-if="shipment.fr_data.service_price_tag_removal_price > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.additional_services.price_tag_removal.title')}:` }}</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.service_price_tag_removal_price) }}</span>
            </div>
            <div v-if="shipment.fr_data.service_invoice_removal_price > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.additional_services.invoice_removal.title')}:` }}</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.service_invoice_removal_price) }}</span>
            </div>
            <div v-if="shipment.fr_data.service_insurance_price > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.additional_services.insurance.title')}:` }}</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.service_insurance_price) }}</span>
            </div>
            <div v-if="totalOtherServicesPrice > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.payment.other_services')}:` }}</span>
              <span class="text-sm">{{ toPrice(totalOtherServicesPrice) }}</span>
            </div>
            <div v-if="transactionFee > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ `${t('site_pro.payment.payment_method_fee')}:` }}</span>
              <span class="text-sm">{{ toPrice(transactionFee) }}</span>
            </div>
            <div v-if="shipment.fr_data.additional_fee > 0" class="flex gap-1 justify-between">
              <span class="text-sm">{{ t('site_pro.payment.additional_fee') }}:</span>
              <span class="text-sm">{{ toPrice(shipment.fr_data.additional_fee) }}</span>
            </div>
          </div>
        </template>
        <div ref="tooltipToggleRef" class="tooltip-toggle">
          <i class="fa-solid fa-question-circle text-neutral3 dark:text-dmText3 cursor-pointer" />
        </div>
      </Tooltip>
    </template>

    <template v-else>
      <div class="tooltip-toggle" @click="toggleTooltip()">
        <i class="fa-solid fa-question-circle text-neutral3 dark:text-dmText3 cursor-pointer" />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { AllPackageInterface } from '~/types/shipzeePro/packagesTypes'
import { useSiteProContentStore } from '~/store/shipzeeProContentStore'
import { useLoaderStore } from '~/store/loader'
import Tooltip from '~/components/Tooltip.vue'
import toPrice from '~/utils/toPrice'
import type { PropType } from 'vue'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'

const { t } = useLang()

const shipzeeProContent = useSiteProContentStore()
const loader = useLoaderStore()

const props = defineProps({
  shipment: {
    type: Object as PropType<AllPackageInterface>,
    required: true,
  },
})

const tooltipToggleRef = ref<HTMLDivElement>()

async function toggleTooltip() {
  await updateShipmentData()

  tooltipToggleRef.value?.click()
}

async function updateShipmentData(): Promise<void> {
  const item: AllPackageInterface = { ...props.shipment }

  loader.setLoader(true)

  if (!item.fr_data) {
    item.fr_data = await $fetch('/api/site-pro/get-forward-request-by-id', {
      method: 'POST',
      body: { id: item.id },
      ...fetchRetryParameters,
      headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
    })
    shipzeeProContent.updateAllItem(item)
  }

  loader.setLoader(false)
}

const shippingFee = computed<number>(() => {
  if (props.shipment.fr_data) {
    return props.shipment.fr_data.fee_shipping + props.shipment.fr_data.fee_service + props.shipment.fr_data.overweight
  }

  return 0
})

const totalOtherServicesPrice = computed<number>(() => {
  if (props.shipment.fr_data) {
    return (
      props.shipment.fr_data.package_additional_service_additional_photos_fee *
        props.shipment.fr_data.package_additional_service_additional_photos_count +
      props.shipment.fr_data.package_additional_service_inspect_package_fee *
        props.shipment.fr_data.package_additional_service_inspect_package_count +
      props.shipment.fr_data.storage_fee
    )
  }

  return 0
})

const transactionFee = computed<number>(() => {
  if (props.shipment.fr_data && props.shipment.fr_data.future_payments) {
    return props.shipment.fr_data.future_payments.reduce(
      (totalFee: number, payment: { payment_processor_fee: number }) => totalFee + payment.payment_processor_fee,
      0
    )
  }

  return 0
})
</script>

<style lang="postcss" scoped>
.all-shipment-price-tooltip-component {
  .tooltip-toggle {
    @apply text-xs text-neutral3 dark:text-dmText3 dark:bg-dmGray1 cursor-pointer;
  }
}
</style>
